"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { useKeyStrikes } from "@/hooks/harmony/use-key-strikes";
import { TICK_EPSILON } from "@/lib/harmony/cursor";
import { gateCrossed, type RhythmGate } from "@/lib/harmony/rhythm";
import { graceMs, takeDueAt } from "@/lib/harmony/take";

/**
 * Traverser une fenêtre porte après porte.
 *
 * ## Ce que ça remplace, et pourquoi
 *
 * `useChordTake` validait la fenêtre **une fois** : on plaquait l'accord, le
 * passage d'origine partait et courait jusqu'au bout quoi qu'on fasse ensuite.
 * C'était la bonne règle tant qu'un niveau demandait un accord tenu.
 *
 * Le niveau trois demande un rythme, et un rythme ne se valide pas d'un seul
 * geste : la gauche frappe sur un et trois, la droite répond sur deux et
 * quatre, et une validation unique laisse le joueur écouter le reste sans rien
 * jouer. La fenêtre est donc découpée aux instants qu'elle contient, et
 * **chaque instant se franchit**.
 *
 * ## La musique attend, mais elle laisse le temps d'arriver
 *
 * Le morceau sonne d'une porte à la suivante. À la porte, il ne s'arrête pas
 * net : il continue **comme si elle avait été franchie**, le temps d'une
 * grâce — voir `graceMs`. Une frappe qui arrive pendant cette grâce ne
 * s'entend pas : le segment d'après était déjà en train de sonner, il
 * continue. Une frappe qui n'arrive pas fait taire la musique et ramène la
 * tête sur la porte ; la frappe tardive repartira de là.
 *
 * Il n'y a donc ni couleur d'erreur, ni compte, ni message : c'est la règle
 * de `take.ts` — on ne joue jamais faux — appliquée au tour de chaque frappe
 * au lieu du tour de chaque accord. Mais il y a désormais un retard, et
 * c'est le premier : avant la grâce, la musique se taisait à la porte quel
 * que soit le retard, et un joueur centré sur le temps entendait un trou une
 * porte sur deux.
 *
 * En avance, on peut l'être : frapper la porte suivante avant la fin du
 * segment n'est pas perdu, la porte s'ouvre d'elle-même à la fin.
 *
 * ## Le segment continue le précédent, ou repart
 *
 * Chaque segment dit s'il **continue** le son en cours ou s'il repart de sa
 * porte. Tant que la musique n'a pas été interrompue — porte franchie
 * pendant le segment ou pendant sa grâce — il continue, et l'écoute ne fait
 * que reculer son point d'arrêt : pas de réattaque, pas de voix relâchées,
 * pas de coupure. Après une grâce écoulée, après un saut de la tête ailleurs,
 * à l'ouverture d'une unité, il repart.
 *
 * Et sa fin est datée depuis **la porte**, pas depuis la frappe : un segment
 * qui continue commence là où le précédent devait finir, quoi que la frappe
 * ait pris de la grâce. Sans cela chaque porte franchie en retard reculerait
 * la grille d'autant, et le morceau ralentirait au fil de la fenêtre.
 *
 * ## Quand la fenêtre commence avant la première frappe
 *
 * L'accompagnement se tait parfois sur le premier temps et n'entre qu'après :
 * c'est le contretemps, et le niveau trois le garde parce qu'il est le morceau.
 * Rien ne peut y être validé, puisqu'il n'y a rien à jouer — mais l'attendre en
 * silence ne dit pas non plus quand entrer. Ce bout-là **se joue tout seul**,
 * du début de la fenêtre à la première frappe : c'est la levée, et c'est elle
 * qui donne le temps qu'on doit prendre. L'écran attend ensuite, comme partout
 * ailleurs.
 *
 * Le silence qui **suit** la dernière frappe se joue de la même façon, et par
 * le même chemin : le dernier segment ne s'arrête pas au bord de la fenêtre
 * mais à la frappe suivante. Voir `untilTick`.
 *
 * ## Une seule porte, et c'est exactement l'ancien comportement
 *
 * Aux niveaux un et deux les deux mains frappent ensemble au début de la
 * fenêtre et nulle part ailleurs : il n'y a qu'une porte, elle demande l'accord
 * entier, et le passage court jusqu'au bout. Rien n'a changé pour eux, ce qui
 * est la raison de découper par les notes du niveau plutôt que par une grille.
 */

export type TakeSegment = {
  readonly id: string;
  readonly startTick: number;
  readonly endTick: number;
  /**
   * Quand il finit de sonner, sur l'horloge de `performance.now()`.
   *
   * Une date et non une durée : ce qui le fait entendre et ce qui le fait
   * défiler partent chacun à leur moment, et les deux doivent finir au même.
   *
   * `null` sur un segment qui repart, jusqu'à ce qu'il ait fini : il part
   * quand il part, et le rendu ne lit pas l'heure. Sa durée, `runningMs`,
   * suffit à qui doit le suivre. Un segment qui continue est daté d'avance,
   * par la porte, et c'est là que la date compte : elle dit ce qu'il en reste.
   */
  readonly endsAt: number | null;
  /**
   * Il continue le son en cours au lieu de repartir de sa porte.
   *
   * Vrai dès que la porte a été franchie pendant que le segment d'avant
   * sonnait encore, grâce comprise.
   */
  readonly continued: boolean;
};

export type RhythmTake = {
  /**
   * La dernière porte franchie, ou `-1` tant qu'on n'a rien joué.
   *
   * C'est ce que la tête de lecture suit et ce qui allume les traits.
   */
  readonly at: number;
  /** Au moins une porte est franchie : le passage a commencé. */
  readonly struck: boolean;
  /** L'accord compte comme pris, pour ce que la lueur du fond en montre. */
  readonly taken: boolean;
  /**
   * Le passage à faire entendre, ou `null` quand rien ne doit sonner : avant
   * la première frappe, ou après une grâce écoulée.
   *
   * D'une porte à la suivante, et de la dernière à la frappe qui ouvre la
   * fenêtre d'après. Sur une fenêtre où l'on vient d'arriver en continuant
   * le son, c'est la levée, même vide : tant que le son est en cours, il y a
   * un segment, sans quoi le silence de la grâce se lirait comme un arrêt.
   */
  readonly segment: TakeSegment | null;
  /**
   * La porte est ouverte et la musique continue par-dessus : jusqu'où, et
   * jusqu'à quand sur l'horloge de `performance.now()`. `null` hors grâce.
   */
  readonly grace: { readonly tick: number; readonly until: number } | null;
  /**
   * La grâce est écoulée sans frappe : la porte où la tête revient.
   *
   * `null` tant que ce n'est pas le cas. Une valeur par grâce écoulée, donc
   * un retour par grâce écoulée.
   */
  readonly haltTick: number | null;
  /**
   * Le tick sous la tête de lecture, et celui où elle s'arrêtera.
   *
   * Les mêmes bornes que `segment`, mais toujours définies : avant la première
   * frappe le segment n'existe pas — il n'y a rien à faire entendre — alors que
   * la tête, elle, est bien quelque part, posée sur la première porte. C'est ce
   * que le rail translate, d'une porte à la suivante.
   */
  readonly fromTick: number;
  readonly toTick: number;
  /** Ce que dure ce segment, en millisecondes d'écoute. */
  readonly runningMs: number;
};

/**
 * Ce que fait le son à cet instant.
 *
 * `off` : rien ne sonne, et la prochaine frappe repart de sa porte. `on` : un
 * segment sonne, ou sa grâce court, et la prochaine frappe le continue.
 * `lapsed` : la grâce s'est écoulée, le son a été coupé et la tête revient
 * sur la porte ; la prochaine frappe repart.
 */
type Sound = "off" | "on" | "lapsed";

type Flow = {
  readonly chordId: string;
  readonly at: number;
  readonly baseline: ReadonlyMap<number, number>;
  /**
   * Le segment en cours a fini de sonner, donc la porte suivante peut
   * s'ouvrir.
   *
   * Vrai à l'arrivée sur une fenêtre : c'est le joueur qui décide quand elle
   * commence, et rien n'a encore à finir.
   */
  readonly ready: boolean;
  readonly sound: Sound;
  /** Où le segment en cours s'arrête, et quand — `null` tant qu'il n'est pas daté. */
  readonly toTick: number;
  readonly endsAt: number | null;
  readonly continued: boolean;
};

export function useRhythmTake({
  gates,
  startTick: windowTick,
  untilTick,
  chordId,
  armed,
  msAt,
  onSettled,
}: {
  readonly gates: readonly RhythmGate[];
  /** Le début de la fenêtre, d'où part la levée quand il y en a une. */
  readonly startTick: number;
  /**
   * Où s'arrête le dernier segment : la frappe suivante, et non la fin de la
   * fenêtre.
   *
   * Les deux ne coïncident pas, parce que les fenêtres ne se touchent pas
   * toujours. Ce qui sépare la fin d'une fenêtre du début de la suivante est du
   * silence écrit, et il se joue : la tête de lecture le traverse à la vitesse
   * du morceau au lieu de se poser à son bord, où plus aucune porte ne peut
   * s'ouvrir. Voir `windowReach`, qui le calcule et raconte le reste.
   */
  readonly untilTick: number;
  /**
   * L'identité de l'accord sous les mains, qui rearme tout : on retraverse
   * chaque fenêtre depuis sa première porte.
   */
  readonly chordId: string;
  /**
   * La fenêtre est sous les mains pour de bon.
   *
   * Faux pendant le décompte d'ouverture : les touches sont là, on peut s'y
   * poser, mais une frappe avant le premier temps lancerait le passage
   * par-dessus le compte.
   */
  readonly armed: boolean;
  /** Un tick du morceau, en millisecondes d'écoute. */
  readonly msAt: (tick: number) => number;
  /**
   * La dernière porte a fini de sonner : la fenêtre est faite.
   *
   * Elle dit **laquelle**, et ce n'est pas une commodité. Le rappel part sur
   * une minuterie, et pendant ce temps la tête de lecture du studio a fini son
   * défilement, donc l'accord ancré a déjà avancé d'un cran. Un appelant qui
   * lirait « où en est-on » au lieu de « qu'est-ce qui vient de se faire »
   * sauterait la fenêtre suivante.
   */
  readonly onSettled: (chordId: string) => void;
}): RhythmTake {
  const { presses, together } = useKeyStrikes();

  /**
   * La levée : ce que la fenêtre joue avant sa première frappe.
   *
   * Nulle quand l'accompagnement entre sur le premier temps, ce qui est le cas
   * ordinaire ; elle vaut le silence écrit quand il entre après.
   */
  const leadTick = gates[0]?.tick ?? untilTick;
  const leadMs = Math.max(msAt(leadTick) - msAt(windowTick), 0);

  /**
   * Où l'on en est, le compte des frappes au moment où la porte s'est
   * ouverte, et ce que fait le son.
   *
   * Tout dans le même état parce que tout change ensemble : la photo du
   * compte est prise **dans la mise à jour qui ouvre la porte**, ce qui est la
   * seule façon qu'aucune frappe ne tombe entre les deux. Prise dans un effet,
   * une frappe arrivée dans la même image serait comptée du mauvais côté et le
   * joueur devrait rejouer.
   *
   * La date de fin d'un segment qui continue est prise au même endroit : c'est
   * la porte qui la fixe, et la porte est connue. Celle d'un segment qui
   * repart ne l'est pas — il part quand il part, et le rendu ne lit pas
   * l'heure — donc elle reste vide ici ; sa minuterie la fixe, et la pose
   * dans l'état au moment où elle sonne, puisque c'est alors qu'elle sert.
   */
  const [state, setState] = useState<Flow>(() => ({
    chordId,
    at: -1,
    baseline: presses,
    ready: leadMs <= 0,
    sound: leadMs > 0 ? "on" : "off",
    toTick: leadMs > 0 ? leadTick : windowTick,
    endsAt: null,
    continued: false,
  }));

  /*
    Même motif que l'ancien `useChordTake` : un état qu'on ajuste pendant le
    rendu quand ce qu'il décrit a changé, plutôt qu'un effet qui daterait tout
    d'une image.

    ## La musique ne va jamais plus vite que ce qui est écrit

    Une porte ne s'ouvre que quand le segment d'avant a **fini de sonner**.
    Sans cette condition, refrapper la basse aussitôt après l'avoir jouée
    faisait sauter la musique à l'instant suivant : on avançait au rythme de
    l'impatience, et une main gauche qui répète quatre fois la même note
    traversait la mesure en une seconde.

    L'anticipation, elle, n'est pas perdue. La frappe en avance reste vraie —
    le compte ne redescend pas — donc la porte s'ouvre d'elle-même à la fin du
    segment. Le joueur peut jouer devant la musique ; c'est la musique qui tient
    le temps.

    Et l'avance est bornée à une porte sans qu'on ait à la compter : franchir
    reprend la photo du compte, donc une rafale de frappes ne vaut que pour la
    porte suivante. Celle d'après demandera une vraie frappe.

    ## Le trou du changement d'accord, refermé sans horloge

    Au changement de fenêtre, seule la position repart : **la photo du compte
    reste celle de la dernière porte franchie**, quelle que soit la fenêtre.

    Qui joue en mesure frappe l'accord suivant *au moment* où l'écran en change,
    et les deux ne tombent pas à la milliseconde : l'écran attend la fin du
    segment, déplace la tête de lecture, attend le retour du transport, puis
    seulement redéclare ce qu'il attend. Une photo prise à ce moment-là
    contiendrait déjà la frappe du joueur, qui serait donc perdue et qu'il
    faudrait rejouer. C'est exactement ce que `STRIKE_GRACE_MS` corrigeait du
    temps où la validation lisait des touches enfoncées.

    La garder de la porte précédente referme le trou sans lire l'heure : entre
    cette porte et la fenêtre suivante, tout ce que le joueur frappe lui
    appartient déjà.

    ## Le son, lui, traverse le changement d'accord

    La fenêtre d'après continue le son si elle commence **là où il s'arrête** :
    c'est le cas quand on y arrive par la dernière porte de la précédente, et
    ce n'est pas le cas quand la tête a été posée ailleurs à la main. Un saut
    dans la minimap repart donc, et l'enchaînement continue, sans que
    personne n'ait à dire lequel des deux vient d'arriver.
  */
  if (state.chordId !== chordId) {
    const from =
      state.sound === "on" && Math.abs(windowTick - state.toTick) <= TICK_EPSILON
        ? state.endsAt
        : null;

    setState({
      chordId,
      at: -1,
      baseline: state.baseline,
      ready: leadMs <= 0,
      sound: from !== null || leadMs > 0 ? "on" : "off",
      toTick: leadMs > 0 ? leadTick : windowTick,
      endsAt: from === null ? null : from + leadMs,
      continued: from !== null,
    });
  } else if (armed && state.ready) {
    const next = state.at + 1;
    const gate = gates[next];

    if (gate !== undefined && gateCrossed(gate, presses, state.baseline, together)) {
      const from = state.sound === "on" ? state.endsAt : null;
      const toTick = gates[next + 1]?.tick ?? untilTick;

      setState({
        chordId,
        at: next,
        baseline: presses,
        ready: false,
        sound: "on",
        toTick,
        endsAt: from === null ? null : from + Math.max(msAt(toTick) - msAt(gate.tick), 0),
        continued: from !== null,
      });
    }
  }

  const current = state.chordId === chordId;
  const at = current ? state.at : -1;
  const last = gates.length - 1;

  /**
   * La fenêtre confirmée, pour cette visite-ci.
   *
   * Par ref et non par état, pour la même raison que dans `useChordTake` :
   * confirmer est un événement, et l'écran est déplacé par ce qu'il déclenche.
   */
  const settled = useRef<string | null>(null);

  // À `-1`, le segment est la levée : du début de la fenêtre à la première
  // frappe. Ailleurs, d'une porte à la suivante.
  const from = at >= 0 ? (gates[at]?.tick ?? windowTick) : windowTick;
  const to = at >= 0 ? (gates[at + 1]?.tick ?? untilTick) : leadTick;
  const runningMs = at >= 0 ? Math.max(msAt(to) - msAt(from), 0) : leadMs;

  /*
    Le segment sonne, puis il rend la main.

    Une minuterie par porte et non plus une seule pour la dernière : c'est elle
    qui tient le temps de la musique, et sans elle une frappe en avance ferait
    sauter le segment. À son terme la porte suivante devient franchissable, et
    sur la dernière il n'y a plus de porte : la fenêtre est faite.

    Une date et non un délai, parce que l'effet se rejoue à chaque fois que
    `onSettled` change d'identité : un délai relatif repartirait de zéro à
    chaque recalcul et la fin reculerait indéfiniment. La date est celle de
    l'état, fixée à la porte.
  */
  const { endsAt } = state;
  const waiting = current && !state.ready;
  const due = useRef<{ readonly key: string; readonly at: number } | null>(null);

  useEffect(() => {
    if (!waiting) {
      return;
    }

    const key = `${chordId}:${at}`;

    // Un segment qui continue est daté par la porte ; un segment qui repart
    // l'est ici, une fois, à son premier passage — et non à chaque fois que
    // l'effet se rejoue, sans quoi sa fin reculerait à chaque recalcul.
    if (endsAt !== null) {
      due.current = { key, at: endsAt };
    } else if (due.current?.key !== key) {
      due.current = { key, at: takeDueAt({ struckAt: performance.now(), holdMs: runningMs }) };
    }

    const ends = due.current.at;

    const timer = window.setTimeout(
      () => {
        setState((prev) =>
          prev.chordId === chordId && prev.at === at ? { ...prev, ready: true, endsAt: ends } : prev,
        );

        if (at === last && settled.current !== chordId) {
          settled.current = chordId;
          onSettled(chordId);
        }
      },
      Math.max(ends - performance.now(), 0),
    );

    return () => window.clearTimeout(timer);
  }, [waiting, chordId, at, last, endsAt, runningMs, onSettled]);

  /**
   * La grâce : la porte est ouverte, le son continue par-dessus.
   *
   * Mesurée sur le segment que la porte ouvre, et traduite en ticks à la
   * vitesse de ce segment-là, pour que la tête déborde exactement là où le
   * son en est. Nulle tant que le segment sonne, nulle après la dernière
   * porte de la fenêtre — la suivante appartient à l'accord d'après, qui la
   * mesurera sur ses propres frappes une fois la tête arrivée chez lui.
   */
  const open = current && state.ready && state.sound === "on";

  const grace = useMemo(() => {
    const gate = gates[at + 1];

    if (!open || endsAt === null || gate === undefined) {
      return null;
    }

    const endTick = gates[at + 2]?.tick ?? untilTick;
    const segmentMs = Math.max(msAt(endTick) - msAt(gate.tick), 0);
    const ms = graceMs(segmentMs);
    const tick = segmentMs > 0 ? gate.tick + ((endTick - gate.tick) * ms) / segmentMs : gate.tick;

    return { tick, until: endsAt + ms };
  }, [open, gates, at, untilTick, msAt, endsAt]);

  /*
    La grâce s'écoule : le son se tait, la tête revient.

    Datée depuis la fin du segment, comme tout le reste, et non depuis
    l'image où la porte s'est ouverte : la même règle que la minuterie du
    segment, pour la même raison.
  */
  const graceUntil = grace?.until ?? null;

  useEffect(() => {
    if (graceUntil === null) {
      return;
    }

    const timer = window.setTimeout(
      () => {
        setState((prev) =>
          prev.chordId === chordId && prev.at === at && prev.ready && prev.sound === "on"
            ? { ...prev, sound: "lapsed" }
            : prev,
        );
      },
      Math.max(graceUntil - performance.now(), 0),
    );

    return () => window.clearTimeout(timer);
  }, [graceUntil, chordId, at]);

  const sounding = current && state.sound === "on";
  const { continued } = state;

  return useMemo(
    () => ({
      at,
      struck: at >= 0,
      // Prise dès la première porte franchie. C'est ce que lit l'annonce de
      // l'accord suivant : elle arrive quand celui-ci a commencé, et non quand
      // il est fini, pour qu'on ait le temps de préparer la main.
      taken: at >= 0,
      segment: sounding
        ? { id: `${chordId}:${at}`, startTick: from, endTick: to, endsAt, continued }
        : null,
      grace,
      haltTick: current && state.sound === "lapsed" ? to : null,
      fromTick: from,
      toTick: to,
      runningMs,
    }),
    [at, chordId, from, to, runningMs, sounding, endsAt, continued, grace, current, state.sound],
  );
}
